import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ReportFilters, SearchQuery } from '../hooks/useReports';
import AdvancedSearchFilter from './AdvancedSearchFilter';
import MobileSearchInterface from './MobileSearchInterface';

interface SimpleSearchInterfaceProps {
  className?: string;
  disabled?: boolean;
}

const SimpleSearchInterface: React.FC<SimpleSearchInterfaceProps> = ({
  className = '',
  disabled = false
}) => {
  const navigate = useNavigate();

  // Handle search queries
  const handleSearch = useCallback((searchQuery: SearchQuery) => {
    const searchParams = new URLSearchParams();
    
    if (searchQuery.text) searchParams.set('text', searchQuery.text);
    if (searchQuery.status) searchParams.set('status', searchQuery.status);
    if (searchQuery.postcode) searchParams.set('postcode', searchQuery.postcode);
    if (searchQuery.tags?.length) searchParams.set('tags', searchQuery.tags.join(','));
    if (searchQuery.categories?.length) searchParams.set('categories', searchQuery.categories.join(','));
    if (searchQuery.dateFrom) searchParams.set('dateFrom', searchQuery.dateFrom.toISOString());
    if (searchQuery.dateTo) searchParams.set('dateTo', searchQuery.dateTo.toISOString());

    navigate(`/treasures?${searchParams.toString()}`);
  }, [navigate]);

  // Handle filter application
  const handleFilter = useCallback((filters: ReportFilters) => {
    const searchParams = new URLSearchParams();
    
    if (filters.status) searchParams.set('status', filters.status);
    if (filters.suburb) searchParams.set('suburb', filters.suburb);
    if (filters.council) searchParams.set('council', filters.council);
    if (filters.postcode) searchParams.set('postcode', filters.postcode);
    if (filters.tags?.length) searchParams.set('tags', filters.tags.join(','));
    if (filters.categories?.length) searchParams.set('categories', filters.categories.join(','));
    if (filters.dateFrom) searchParams.set('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) searchParams.set('dateTo', filters.dateTo.toISOString());

    navigate(`/treasures?${searchParams.toString()}`);
  }, [navigate]);

  // Handle faceted search (redirect to treasures page)
  const handleFacetedSearch = useCallback((filters: Omit<ReportFilters, 'startAfter' | 'limit'>) => {
    const searchParams = new URLSearchParams();
    
    if (filters.status) searchParams.set('status', filters.status);
    if (filters.suburb) searchParams.set('suburb', filters.suburb);
    if (filters.council) searchParams.set('council', filters.council);
    if (filters.postcode) searchParams.set('postcode', filters.postcode);
    if (filters.tags?.length) searchParams.set('tags', filters.tags.join(','));
    if (filters.categories?.length) searchParams.set('categories', filters.categories.join(','));
    if (filters.dateFrom) searchParams.set('dateFrom', filters.dateFrom.toISOString());
    if (filters.dateTo) searchParams.set('dateTo', filters.dateTo.toISOString());

    navigate(`/treasures?${searchParams.toString()}`);
  }, [navigate]);

  return (
    <div className={className}>
      {/* Desktop Search Interface */}
      <div className="hidden md:block">
        <AdvancedSearchFilter
          onSearch={handleSearch}
          onFilter={handleFilter}
          onFacetedSearch={handleFacetedSearch}
          isLoading={disabled}
          isAdmin={false}
        />
      </div>

      {/* Mobile Search Interface */}
      <div className="block md:hidden">
        <MobileSearchInterface
          onSearch={handleSearch}
          onFilter={handleFilter}
          onFacetedSearch={handleFacetedSearch}
          isLoading={disabled}
        />
      </div>
    </div>
  );
};

export default SimpleSearchInterface;